import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { TableSkeleton, Empty, ErrorBanner, Badge, Segmented } from '../../components/ui/Primitives.js';
import { formatMoney, formatDuration, formatNumber } from '../../lib/format.js';
import { SpendBreakdown } from './SpendBreakdown.js';

/**
 * Where the money went, in two tiles side by side (docs/26 E4).
 *
 * The left tile answers it three ways -- by kind of destination, by extension,
 * by cost centre -- one at a time. They are three views of the same sum, and
 * four tiles of them pushed everything else on the dashboard below the fold.
 * The right tile is the calls that cost most, scrolling inside itself so ten
 * rows cannot make the pair uneven.
 */

interface TopCall {
  id: string; started_at: string; direction: string;
  destination_e164: string | null; to_number: string | null;
  duration_sec: number; billable_sec: number;
  spend: string | null; currency: string | null;
  extension_number: string | null; extension_name: string | null;
}
interface TopExtension {
  id: string; number: string; display_name: string;
  calls: number; billable_sec: number; spend: string | null; currency: string | null;
}
interface TopGroup {
  id: string | null; name: string; code: string | null;
  calls: number; extensions: number; billable_sec: number;
  spend: string | null; currency: string | null;
}
interface Insights {
  window: { from: string; days: number };
  top_calls: TopCall[];
  top_extensions: TopExtension[];
  top_groups: TopGroup[];
}

export const useSpendInsights = (tenantId: string, days = 30) =>
  useQuery({
    queryKey: ['cdr-insights', tenantId, days],
    queryFn: () => api.get<Insights>(`/v1/tenants/${tenantId}/cdrs/insights?days=${days}`),
    enabled: !!tenantId,
  });

/**
 * A bar drawn in the row itself.
 *
 * Ranked money is much easier to read as length than as digits: the gap
 * between first and second is the thing worth seeing, and comparing "R 412,80"
 * to "R 388,15" digit by digit is work.
 */
function Share({ value, max }: { value: number; max: number }) {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div className="arc-share" aria-hidden="true">
      <div className="arc-share-fill" style={{ width: `${pct}%` }} />
    </div>
  );
}

const num = (s: string | null): number => (s === null ? 0 : Number(s));

type View = 'destination' | 'extension' | 'centre';
const VIEWS: Array<[View, string]> = [
  ['destination', 'Destination'], ['extension', 'Extension'], ['centre', 'Cost centre'],
];

export function SpendInsights({ tenantId, days = 30 }: { tenantId: string; days?: number }) {
  const q = useSpendInsights(tenantId, days);
  const [view, setView] = useState<View>('destination');
  if (q.error) return <ErrorBanner error={q.error} />;

  const calls = q.data?.top_calls ?? [];
  const exts = q.data?.top_extensions ?? [];
  const groups = q.data?.top_groups ?? [];
  const maxExt = Math.max(0, ...exts.map((e) => num(e.spend)));
  const maxGroup = Math.max(0, ...groups.map((g) => num(g.spend)));

  return (
    <div className="arc-grid arc-grid-2">
      <div className="arc-card">
        <div className="arc-card-head">
          <h2 className="arc-h2">Spend by</h2>
          <Segmented label="Spend by" value={view} options={VIEWS} onChange={setView} />
          <span className="arc-subtle">outbound, last {days} days</span>
        </div>

        {view === 'destination' && (
          <div className="arc-card-body">
            <SpendBreakdown tenantId={tenantId} days={days} bare />
          </div>
        )}

        {view === 'extension' && (
          <>
            <div className="arc-table-wrap arc-scroll-y">
              <table className="arc-table">
                <thead>
                  <tr>
                    <th>Extension</th><th style={{ width: '32%' }}>Share</th>
                    <th className="arc-td-num">Calls</th><th className="arc-td-num">Spend</th>
                  </tr>
                </thead>
                {q.isLoading ? <TableSkeleton cols={4} /> : (
                  <tbody>
                    {exts.map((e) => (
                      <tr key={e.id}>
                        <td>
                          <span className="arc-mono">{e.number}</span>
                          <span style={{ marginLeft: 7, fontWeight: 550 }}>{e.display_name}</span>
                        </td>
                        <td><Share value={num(e.spend)} max={maxExt} /></td>
                        <td className="arc-td-num arc-muted">{formatNumber(e.calls)}</td>
                        <td className="arc-td-num" style={{ fontWeight: 600 }}>
                          {formatMoney(e.spend, e.currency ?? 'ZAR')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                )}
              </table>
            </div>
            {!q.isLoading && exts.length === 0 && (
              <Empty icon="users" title="No outbound spend" hint="Nobody has made a billable call yet." />
            )}
          </>
        )}

        {view === 'centre' && (
          <>
            <div className="arc-table-wrap arc-scroll-y">
              <table className="arc-table">
                <thead>
                  <tr>
                    <th>Cost centre</th><th style={{ width: '30%' }}>Share</th>
                    <th className="arc-td-num">Seats</th><th className="arc-td-num">Spend</th>
                  </tr>
                </thead>
                {q.isLoading ? <TableSkeleton cols={4} /> : (
                  <tbody>
                    {groups.map((g) => (
                      <tr key={g.id ?? 'unassigned'}>
                        <td style={{ fontWeight: 550 }}>
                          {g.name}
                          {g.code && <span className="arc-subtle" style={{ marginLeft: 7 }}>{g.code}</span>}
                          {/* Named, not hidden: unallocated spend is usually the
                              first thing a finance team wants to chase down. */}
                          {g.id === null && (
                            <span className="arc-subtle" style={{ marginLeft: 7 }}>no cost centre</span>
                          )}
                        </td>
                        <td><Share value={num(g.spend)} max={maxGroup} /></td>
                        <td className="arc-td-num arc-muted">{g.extensions}</td>
                        <td className="arc-td-num" style={{ fontWeight: 600 }}>
                          {formatMoney(g.spend, g.currency ?? 'ZAR')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                )}
              </table>
            </div>
            {!q.isLoading && groups.length === 0 && (
              <Empty icon="accounts" title="No cost centre spend"
                hint="Assign extensions to cost centres to see spend split by department." />
            )}
          </>
        )}
      </div>

      <div className="arc-card">
        <div className="arc-card-head">
          <h2 className="arc-h2">Most expensive calls</h2>
          <span className="arc-subtle">last {days} days</span>
        </div>
        <div className="arc-table-wrap arc-scroll-y">
          <table className="arc-table">
            <thead>
              <tr>
                <th>When</th><th>Extension</th><th>Destination</th>
                <th className="arc-td-num">Cost</th>
              </tr>
            </thead>
            {q.isLoading ? <TableSkeleton cols={4} /> : (
              <tbody>
                {calls.map((c) => (
                  <tr key={c.id}>
                    <td className="arc-muted">
                      {new Date(c.started_at).toLocaleString('en-ZA', {
                        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                      })}
                    </td>
                    <td>
                      {c.extension_number
                        ? <><span className="arc-mono">{c.extension_number}</span>
                            {c.extension_name ? ` · ${c.extension_name}` : ''}</>
                        : <span className="arc-subtle">—</span>}
                    </td>
                    <td className="arc-mono">
                      {c.destination_e164 ?? c.to_number ?? <span className="arc-subtle">—</span>}
                      {c.direction !== 'outbound' && (
                        <Badge tone="muted">{c.direction}</Badge>
                      )}
                    </td>
                    <td className="arc-td-num">
                      <div className="arc-stack" style={{ alignItems: 'flex-end' }}>
                        <span style={{ fontWeight: 600 }}>
                          {formatMoney(c.spend, c.currency ?? 'ZAR')}
                        </span>
                        {/* Billable seconds, not wall-clock: they differ whenever
                            a carrier bills in 60-second increments, and the
                            billable figure is the one the amount was computed
                            from. Sitting under the amount, it reads as the
                            explanation for it. */}
                        <span className="arc-subtle" style={{ fontSize: 11 }}>
                          {formatDuration(c.billable_sec)}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {!q.isLoading && calls.length === 0 && (
          <Empty icon="rates" title="No billable calls yet"
            hint="Outbound calls appear here once they have been rated." />
        )}
      </div>
    </div>
  );
}
